import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  Skeleton,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import {
  useGetSalesInsightsQuery,
  useLazyGetSalesSegmentTransactionsQuery,
  useLazyGetUntaggedRenewalTransactionsQuery,
} from '../services/dashboard.endpoints';
import {
  SalesPeriodDetailPanel,
  SalesPeriodPoint,
  SalesSegmentTransactionsDialog,
  SegmentDrilldownState,
  SaleCategory,
  UntaggedRenewalsDialog,
} from './SalesChartDrilldown';
import {
  brand,
  dashboardSalesStyles,
  kpiCardSx,
  KpiAccent,
} from '../dashboardSales.styles';

const SALE_TYPE_COLORS = {
  initial: '#1976d2',
  renewal: '#2e7d32',
  plan_change: '#ed6c02',
  other: '#757575',
} as const;

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function truncateAxisLabel(label: string, maxLength = 36) {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - 1)}…`;
}

function renderCategoryAxisTick({
  x = 0,
  y = 0,
  payload,
  maxLabelLength,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  maxLabelLength?: number;
}) {
  const label = maxLabelLength
    ? truncateAxisLabel(payload?.value ?? '', maxLabelLength)
    : payload?.value ?? '';

  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill="#37474F"
      fontSize={13}
      fontWeight={500}
    >
      {label}
    </text>
  );
}

function KpiCard({
  label,
  value,
  sub,
  accent,
  icon,
}: {
  label: string;
  value: string;
  sub?: string;
  accent: KpiAccent;
  icon: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={kpiCardSx(accent)}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 0.6 }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              fontWeight={700}
              className="kpi-value"
              sx={{ mt: 0.5 }}
            >
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary">
                {sub}
              </Typography>
            )}
          </Box>
          <Box sx={{ opacity: 0.35, mt: 0.5 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ChartCard({
  title,
  caption,
  accent,
  children,
}: {
  title: string;
  caption?: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <Card sx={dashboardSalesStyles.chartCard}>
      <Box sx={dashboardSalesStyles.chartCardHeader(accent)}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: brand.slate }}
        >
          {title}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary" display="block">
            {caption}
          </Typography>
        )}
      </Box>
      <CardContent sx={{ pt: 2 }}>{children}</CardContent>
    </Card>
  );
}

export interface SalesInsightsFilters {
  from: dayjs.Dayjs | null;
  to: dayjs.Dayjs | null;
  productIds: string[];
}

const SalesInsightsCharts = ({
  filters,
}: {
  filters: SalesInsightsFilters;
}) => {
  const [drilldown, setDrilldown] = useState<SegmentDrilldownState | null>(
    null,
  );
  const [pinnedPeriod, setPinnedPeriod] = useState<SalesPeriodPoint | null>(
    null,
  );
  const [untaggedDialogOpen, setUntaggedDialogOpen] = useState(false);
  const [
    fetchUntagged,
    {
      data: untaggedData,
      isLoading: untaggedLoading,
      isFetching: untaggedFetching,
    },
  ] = useLazyGetUntaggedRenewalTransactionsQuery();
  const [
    fetchSegment,
    {
      data: segmentData,
      isLoading: segmentLoading,
      isFetching: segmentFetching,
    },
  ] = useLazyGetSalesSegmentTransactionsQuery();

  const requestBody = useMemo(() => {
    if (!filters.from || !filters.to) return null;
    return {
      from: filters.from.format('YYYY-MM-DD'),
      to: filters.to.format('YYYY-MM-DD'),
      productIds: filters.productIds,
    };
  }, [filters]);

  const { data, isLoading, isFetching, error } = useGetSalesInsightsQuery(
    requestBody!,
    { skip: !requestBody },
  );

  const topProductsChartData = useMemo(
    () =>
      [...(data?.revenueByProduct ?? [])].sort((a, b) => b.revenue - a.revenue),
    [data?.revenueByProduct],
  );

  const handleViewTransactions = useCallback(
    (
      period: string,
      category: SaleCategory,
      periodLabel: string,
      count: number,
    ) => {
      if (!requestBody || !data?.granularity) return;
      setDrilldown({ period, category, periodLabel, expectedCount: count });
      fetchSegment({
        from: requestBody.from,
        to: requestBody.to,
        productIds: requestBody.productIds,
        period,
        granularity: data.granularity,
        category,
      });
    },
    [requestBody, data?.granularity, fetchSegment],
  );

  const handleCloseDrilldown = useCallback(() => {
    setDrilldown(null);
  }, []);

  const handleOpenUntaggedDialog = useCallback(() => {
    if (!requestBody) return;
    setUntaggedDialogOpen(true);
    fetchUntagged({
      from: requestBody.from,
      to: requestBody.to,
      productIds: requestBody.productIds,
    });
  }, [requestBody, fetchUntagged]);

  const handleCloseUntaggedDialog = useCallback(() => {
    setUntaggedDialogOpen(false);
  }, []);

  const handleBarClick = useCallback((barData: unknown) => {
    const d = barData as SalesPeriodPoint & { payload?: SalesPeriodPoint };
    const row = d.payload ?? d;
    if (row?.period) setPinnedPeriod(row);
  }, []);

  useEffect(() => {
    setPinnedPeriod(null);
  }, [requestBody?.from, requestBody?.to, requestBody?.productIds]);

  if (!requestBody) return null;

  if (isLoading || isFetching) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton
                variant="rounded"
                height={88}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          ))}
        </Grid>
        <Skeleton
          variant="rounded"
          height={320}
          sx={{ borderRadius: 2, mb: 2 }}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        Could not load chart data. Try adjusting the date range.
      </Alert>
    );
  }

  const hasTimeSeries = data.salesOverTime.length > 0;
  const hasProducts = topProductsChartData.length > 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={3}>
          <KpiCard
            label="Transactions"
            value={String(data.totals.count)}
            accent="slate"
            icon={<ReceiptLongOutlinedIcon />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <KpiCard
            label="Revenue"
            value={formatCurrency(data.totals.revenue)}
            accent="teal"
            icon={<PaidOutlinedIcon />}
          />
        </Grid>
      </Grid>

      {data.untaggedRenewalHint > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleOpenUntaggedDialog}
            >
              View details
            </Button>
          }
        >
          {data.untaggedRenewalHint} subscription charge(s) in this period lack
          a renewal tag and may be miscategorized.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ChartCard
            title="Sales by period"
            caption="Stacked bars = sale count by type · Line = revenue · Click a bar to drill down"
            accent={brand.purple}
          >
            {hasTimeSeries ? (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={data.salesOverTime}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      yAxisId="count"
                      allowDecimals={false}
                      tick={{ fontSize: 11 }}
                      width={36}
                    />
                    <YAxis
                      yAxisId="revenue"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `$${v}`}
                      width={48}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => {
                        if (name === 'Revenue') {
                          return [formatCurrency(value), name];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      yAxisId="count"
                      dataKey="initial"
                      stackId="sales"
                      fill={SALE_TYPE_COLORS.initial}
                      name="New subs"
                      cursor="pointer"
                      onClick={handleBarClick}
                    />
                    <Bar
                      yAxisId="count"
                      dataKey="renewal"
                      stackId="sales"
                      fill={SALE_TYPE_COLORS.renewal}
                      name="Renewals"
                      cursor="pointer"
                      onClick={handleBarClick}
                    />
                    <Bar
                      yAxisId="count"
                      dataKey="plan_change"
                      stackId="sales"
                      fill={SALE_TYPE_COLORS.plan_change}
                      name="Plan changes"
                      cursor="pointer"
                      onClick={handleBarClick}
                    />
                    <Bar
                      yAxisId="count"
                      dataKey="other"
                      stackId="sales"
                      fill={SALE_TYPE_COLORS.other}
                      name="Other"
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={handleBarClick}
                    />
                    <Line
                      yAxisId="revenue"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#6a1b9a"
                      strokeWidth={2}
                      dot={false}
                      name="Revenue"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <SalesPeriodDetailPanel
                  period={pinnedPeriod}
                  onViewTransactions={handleViewTransactions}
                  onClose={() => setPinnedPeriod(null)}
                />
              </>
            ) : (
              <Typography color="text.secondary" py={4} textAlign="center">
                No sales in this date range
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <ChartCard title="Sale mix" accent="#1976d2">
            {data.subscriptionTotals.some((s) => s.count > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={data.subscriptionTotals}
                  layout="vertical"
                  margin={{ left: 4, right: 24, top: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={168}
                    tickLine={false}
                    axisLine={false}
                    tick={renderCategoryAxisTick}
                  />
                  <Tooltip formatter={(v: number) => [v, 'Count']} />
                  <Bar
                    dataKey="count"
                    fill={brand.teal}
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" py={3} textAlign="center">
                No data
              </Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <ChartCard title="Top products (revenue)" accent={brand.teal}>
            {hasProducts ? (
              <ResponsiveContainer
                width="100%"
                height={Math.max(280, topProductsChartData.length * 36)}
              >
                <BarChart
                  data={topProductsChartData}
                  layout="vertical"
                  margin={{ left: 4, right: 24, top: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <YAxis
                    type="category"
                    dataKey="productName"
                    width={220}
                    tickLine={false}
                    axisLine={false}
                    tick={(props) =>
                      renderCategoryAxisTick({ ...props, maxLabelLength: 42 })
                    }
                  />
                  <Tooltip
                    formatter={(v: number) => [formatCurrency(v), 'Revenue']}
                  />
                  <Bar
                    dataKey="revenue"
                    fill={brand.teal}
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" py={3} textAlign="center">
                No data
              </Typography>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <SalesSegmentTransactionsDialog
        open={!!drilldown}
        drilldown={drilldown}
        transactions={segmentData?.transactions ?? []}
        loading={segmentLoading || segmentFetching}
        onClose={handleCloseDrilldown}
      />
      <UntaggedRenewalsDialog
        open={untaggedDialogOpen}
        transactions={untaggedData?.transactions ?? []}
        loading={untaggedLoading || untaggedFetching}
        onClose={handleCloseUntaggedDialog}
      />
    </Box>
  );
};

export default SalesInsightsCharts;
